import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { GlobalContext } from '../../providers/GlobalProvider';
import {
  ExportHubAPI,
  DatabaseHistoryEntry,
  DatabaseHistoryKind,
} from 'api/ExportHubAPI';

interface Props {
  databaseId: string;
}

const PAGE_SIZE = 50;

const RISK_TONE: Record<string, string> = {
  Critical: 'eh-risk-critical',
  High: 'eh-risk-high',
  Medium: 'eh-risk-medium',
  Low: 'eh-risk-low',
};

const KIND_META: Record<
  DatabaseHistoryKind,
  { label: string; icon: React.ReactNode }
> = {
  activity: {
    label: 'Edit',
    icon: <EditOutlinedIcon style={{ fontSize: 16 }} />,
  },
  screening: {
    label: 'Screening',
    icon: <PolicyOutlinedIcon style={{ fontSize: 16 }} />,
  },
  run: {
    label: 'Run',
    icon: <PlayCircleOutlineIcon style={{ fontSize: 16 }} />,
  },
  import: {
    label: 'Import',
    icon: <FileUploadOutlinedIcon style={{ fontSize: 16 }} />,
  },
};

const KIND_FILTERS: Array<{ key: 'all' | DatabaseHistoryKind; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'activity', label: 'Edits' },
  { key: 'screening', label: 'Screenings' },
  { key: 'run', label: 'Runs' },
  { key: 'import', label: 'Imports' },
];

/** Titlecase a snake_case action ("add_child" → "Add child"). */
const humanize = (action: string): string => {
  const spaced = action.replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
};

/**
 * A database's merged timeline — product/license edits, screenings,
 * classification runs, and imports, newest first. Reached from the History icon
 * in the database header.
 */
const DatabaseHistory: React.FC<Props> = ({ databaseId }) => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const groupId = cur_user?.selected_team?.id;

  const [entries, setEntries] = useState<DatabaseHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [kind, setKind] = useState<'all' | DatabaseHistoryKind>('all');

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const page = await ExportHubAPI.getDatabaseHistory(databaseId, groupId, {
        limit: PAGE_SIZE,
      });
      setEntries(page.entries);
      setTotal(page.total);
    } catch (err) {
      console.error('Failed to load database history:', err);
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [databaseId, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  // Paging is server-side over the whole timeline; the kind chips filter what's
  // already loaded, so the count they show is "of the entries fetched so far".
  const loadMore = async (): Promise<void> => {
    if (!groupId || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await ExportHubAPI.getDatabaseHistory(databaseId, groupId, {
        limit: PAGE_SIZE,
        offset: entries.length,
      });
      setEntries((prev) => [...prev, ...page.entries]);
      setTotal(page.total);
    } catch (err) {
      console.error('Failed to load more history:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const openSubject = (e: DatabaseHistoryEntry): void => {
    if (!e.subject_id) return;
    if (e.subject_type === 'product') {
      history.push(
        `/export-hub/databases/${databaseId}/products/${e.subject_id}`,
      );
    } else if (e.subject_type === 'license') {
      history.push(`/export-hub/databases/${databaseId}/licenses`);
    }
  };

  const shown = kind === 'all' ? entries : entries.filter((e) => e.kind === kind);

  if (loading) {
    return (
      <div className='export-hub-panel'>
        <div className='eh-table-skeleton'>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='eh-panel'>
      <div className='eh-panel-title eh-panel-title-flex'>
        <span>
          <HistoryOutlinedIcon
            style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}
          />
          Database History
        </span>
        <span className='eh-panel-note'>
          {total} entr{total === 1 ? 'y' : 'ies'}
        </span>
      </div>

      <div className='eh-hist-filters'>
        {KIND_FILTERS.map((f) => (
          <button
            key={f.key}
            type='button'
            className={`eh-seg-btn ${kind === f.key ? 'active' : ''}`}
            onClick={() => setKind(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className='eh-detail-note'>
          {entries.length === 0
            ? 'Nothing has happened in this database yet.'
            : 'No entries of that kind in what has loaded so far.'}
        </div>
      ) : (
        <div className='eh-hist-list'>
          {shown.map((e) => {
            const meta = KIND_META[e.kind];
            const clickable = !!e.subject_id;
            const Row = clickable ? 'button' : 'div';
            return (
              <Row
                key={`${e.kind}-${e.id}`}
                {...(clickable
                  ? { type: 'button' as const, onClick: () => openSubject(e) }
                  : {})}
                className={`eh-hist-row ${clickable ? 'clickable' : ''}`}
              >
                <span className={`eh-hist-icon ${e.kind}`}>{meta.icon}</span>
                <span className='eh-hist-body'>
                  <span className='eh-hist-top'>
                    <span className='eh-hist-action'>{humanize(e.action)}</span>
                    {!!e.subject_name && (
                      <span className='eh-hist-subject'>{e.subject_name}</span>
                    )}
                    {!!e.risk && (
                      <span className={`eh-risk-tag ${RISK_TONE[e.risk] || ''}`}>
                        {e.risk}
                      </span>
                    )}
                  </span>
                  <span className='eh-hist-message'>{e.message}</span>
                </span>
                <span className='eh-hist-meta'>
                  <span className='eh-hist-kind'>{meta.label}</span>
                  <span
                    className='eh-hist-when'
                    title={moment(e.created_at).format('MM/DD/YYYY h:mm A')}
                  >
                    {moment(e.created_at).format('MM/DD/YYYY h:mm A')}
                  </span>
                  {!!e.actor_name && (
                    <span className='eh-hist-actor'>{e.actor_name}</span>
                  )}
                </span>
              </Row>
            );
          })}
        </div>
      )}

      {entries.length < total && (
        <div className='eh-hist-more'>
          <button
            type='button'
            className='btn-outline'
            onClick={loadMore}
            disabled={loadingMore}
            style={{ opacity: loadingMore ? 0.6 : 1 }}
          >
            {loadingMore
              ? 'Loading…'
              : `Load more (${entries.length} of ${total})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default DatabaseHistory;
